import { useState } from "react";
import { differenceInDays, isPast } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { createFilterProps, typeMapper } from "utility";
import { PAGE_COUNT } from "constants/common";

import { headData, headDataSlider } from "./AccountSummaryData";
import { Filters } from "./filters/Filters";
import { FtTable, FtTableHeadData } from "components/common/table/FtTable";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { useRequestDispatch } from "store/requestHooks";

import { AccountInfo } from "store/account/account-state";
import { changeAccount } from "store/account/account-slice";
import { FilterProps } from "store/common";
import { clearSlide } from "store/common/common-slice";
import { FetchAutoPay, fetchAutoPay } from "store/payments/payments-action";
import { setFilters as setPaymentFilters } from "store/paymentActivity/paymentActivity-slice";
import {
  FetchSchedulePayments,
  fetchSchedulePayments,
} from "store/schedulePayments/schedulePayments-action";
import {
  selectAllAccounts,
  selectedAccountInfo,
} from "store/account/account-selector";

import classes from "./AccountSummary.module.scss";
import {
  FetchPayments,
  fetchPayments,
} from "store/paymentActivity/paymentActivity-action";
import { ScheduledPaymentsFilter } from "store/schedulePayments/schedulePayments-state";

interface AccountSummaryProps {
  hideControls?: boolean;
  slider?: boolean;
}

export const AccountSummary: React.FC<AccountSummaryProps> = ({
  hideControls,
  slider,
}) => {
  const { t } = useTranslation(["common", "account"]);
  const navigate = useNavigate();

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();

  const [openDialog, setOpenDialog] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [filterStatus, setFilterStatus] = useState<FilterProps>({});

  const { filters } = useRootSelector((state) => state.accountSummary);
  const { client } = useRootSelector((state) => state.config);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const allAccounts = useRootSelector(selectAllAccounts);

  const filterClickHandler = () => {
    setOpenDialog(true);
  };

  const wrapperClass = [classes.Wrapper];
  if (slider) {
    wrapperClass.push(classes.SelfContainer);
  }

  const getTableItem = (data: AccountInfo[]) => {
    const transformedItem = data.map((single: AccountInfo) => {
      return {
        ...single,
        status: typeMapper(single?.status!, "PaymentStatusReverse"),
        dueDate: t(`common:date_format`, {
          value: single.dueDate,
        }),
        amountPaid: t(`common:currency_format`, {
          value: single.amountPaid,
        }),
        amountDue: t(`common:currency_format`, {
          value: single.amountDue,
        }),
        paymentMethodName: single.paymentMethodName
          ? single.paymentMethodName
          : "",
      };
    });
    return transformedItem;
  };

  const getLabelTranslation = (id: string) => {
    return t(`paymentactivity:${id}`);
  };

  const getHeadObject = (head: FtTableHeadData[]) => {
    const transformedHeadItem = head.map((single: FtTableHeadData) => {
      return {
        ...single,
        label: single.label ? single.label : getLabelTranslation(single.id),
      };
    });
    return transformedHeadItem;
  };

  const rowClickhandler = (data: AccountInfo) => {
    const selectedAccount = allAccounts.filter(
      (single: AccountInfo) => single.accountNumber === data.accountNumber,
    )[0];

    const paymentFilter: FilterProps = {
      accountNumber: selectedAccount.accountNumber,
    };

    dispatch(changeAccount(selectedAccount.id));
    dispatch(setPaymentFilters(paymentFilter));
    requestDispatch<FetchAutoPay>(fetchAutoPay);
    requestDispatch<FetchSchedulePayments, ScheduledPaymentsFilter>(
      fetchSchedulePayments,
      {
        accountInformationId: selectedAccount.id!,
        status: [0, 1],
      },
    );
    requestDispatch<FetchPayments, FilterProps>(fetchPayments, paymentFilter);
    dispatch(clearSlide());
  };

  const filterResult = () => {
    let res = allAccounts;

    res = res.filter(
      (account) => account.accountNumber !== accountInfo?.accountNumber,
    );

    if (!!filterStatus.searchKeyword) {
      res = allAccounts.filter(
        (single) =>
          !!filterStatus.searchKeyword &&
          single.accountNumber.match(filterStatus.searchKeyword),
      );
    }

    if (!!filterStatus.pastDue) {
      res = res.filter((account) => isPast(new Date(account.dueDate)));
    }

    if (!!filterStatus.dueIn5days) {
      res = res.filter((account) => {
        const due5Res = differenceInDays(new Date(account.dueDate), new Date());
        return due5Res >= 0 && due5Res <= 5;
      });
    }
    return res;
  };

  const accounts = allAccounts ? filterResult() : [];

  return (
    <section className={wrapperClass.join(" ")}>
      {openDialog && (
        <Filters
          filters={filters}
          slider={slider}
          setOpenDialog={setOpenDialog}
        ></Filters>
      )}
      {slider ? (
        !hideControls && (
          <div className={classes.SliderHeader}>
            <div className={classes.FieldWrapper}>
              <Filters
                contentOnly={true}
                filters={filters}
                slider={slider}
                setOpenDialog={setOpenDialog}
                filterEvent={setFilterStatus}
              ></Filters>
            </div>
          </div>
        )
      ) : (
        <div className={classes.Header}>
          <div className={classes.HeaderButtonWrapper}>
            <ft-button
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("common:back_label")}
              outlined
              onClick={() => {
                navigate(`/${client.customerKey}`);
              }}
            ></ft-button>
          </div>
          <div className={classes.HeaderTitle}>
            {t("account:account_summary")}
          </div>
          <div className={classes.Filter} onClick={filterClickHandler}>
            <ft-button
              class={["outline-button", classes.WebFilterButton].join(" ")}
              label={t("common:filters")}
              outlined
              icon={"filter_list"}
              trailingIcon
            ></ft-button>
            <ft-icon class={classes.MobileFIlterButton}>filter_list</ft-icon>
          </div>
        </div>
      )}
      <div className={[classes.TableWraper, "pointer"].join(" ")}>
        <FtTable
          showHeader={slider ? false : true}
          dataAction={() => {}}
          headData={getHeadObject(slider ? headDataSlider : headData)}
          tableData={getTableItem(
            accounts.slice((pageNum - 1) * PAGE_COUNT, pageNum * PAGE_COUNT),
          )}
          pageNum={pageNum}
          autoHeight={slider ? true : false}
          fillHeight={slider ? false : true}
          pagination={allAccounts.length > PAGE_COUNT ? true : false}
          totalCount={accounts.length}
          createFilterProps={createFilterProps}
          filters={filters}
          rowClickhandler={rowClickhandler}
          expandedView={false}
          paginationAction={setPageNum}
        />
      </div>
    </section>
  );
};
